import { computed, type MaybeRefOrGetter } from 'vue'
import { useHead, useSeoMeta } from '#imports'
import {
  getFirstAttributeValue,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'
import { useI18n } from 'vue-i18n'
import { generateProductGroupSchema, sanitizeCanonical } from '~/utils/seo'
import { getProductDetailRoute } from '~/utils/route'
import { normalizeSize } from '../types/variant'
import { getAdvancedAttributeFirstValue } from '../utils/advancedAttribute'
import { useImage } from '#imports'
import { useRuntimeConfig } from '#app/nuxt'
import { useRoute } from '#app/composables/router'
import { useCurrentShop } from '#storefront/composables'
import useRouteHelpers from '../composables/useRouteHelpers'
import usePdpHeadLinks from '../composables/usePdpHeadLinks'

interface UsePdpSeoOptions {
  product: Product
  productName: string
  productLongDescription: string
  price: any
  productVariants: Variant[]
  productAlternateNames: MaybeRefOrGetter<
    Record<string, string> | null | undefined
  >
}

export default function usePdpSeo(options: UsePdpSeoOptions) {
  const { toLocalePath } = useRouteHelpers()
  const config = useRuntimeConfig()
  const i18n = useI18n()
  const img = useImage()
  const route = useRoute()
  const { locale, currency, country } = useCurrentShop().value

  const {
    product,
    productName,
    productLongDescription,
    price,
    productVariants,
    productAlternateNames,
  } = options

  const hasSizeVariantUrl = computed(() => {
    const size = route.query.size
    return typeof size === 'string' && size.length > 0
  })

  const seoTitle = i18n.t('pdp.title_template', {
    productName,
    productColor: getFirstAttributeValue(product.attributes, 'color')?.label,
  })
  const seoDescription =
    productLongDescription || i18n.t('pdp.seo.description', { productName })

  const canonicalBaseUrl = computed(() =>
    sanitizeCanonical(
      `${config.public.baseUrl}${toLocalePath(getProductDetailRoute(product))}`,
    ),
  )

  // og:url must match the rendered rel="canonical" (set in usePdpHeadLinks),
  // which appends ?size= on size-variant URLs. Mirror that here so a shared
  // size-variant link doesn't point to a different URL than the canonical.
  const canonicalUrl = computed(() =>
    hasSizeVariantUrl.value
      ? `${canonicalBaseUrl.value}?size=${encodeURIComponent(normalizeSize(route.query.size as string))}`
      : canonicalBaseUrl.value,
  )

  // og:image — absolute URL for the product's first image (R8). getImage can
  // return a provider-relative path, so prefix the base URL when it isn't
  // already absolute.
  const ogImageUrl = computed(() => {
    const hash = product?.images?.[0]?.hash
    if (!hash) {
      return undefined
    }
    const url = img?.getImage(hash, {
      sizes: 'sm:100vw md:100vw',
      modifiers: { quality: '75' },
    })?.url
    if (!url) {
      return undefined
    }
    return url.startsWith('http') ? url : `${config.public.baseUrl}${url}`
  })

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    robots: () => (hasSizeVariantUrl.value ? 'noindex,follow' : 'index,follow'),
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogUrl: () => canonicalUrl.value,
    ogImage: () => ogImageUrl.value,
  })

  // Resolve the price value reactively. `price` may be a ref/computed (whose
  // value tracks the active size variant) or a plain object; unwrap defensively
  // so the meta tags reflect the variant selected via the ?size= URL on SSR and
  // stay in sync when the size changes client-side.
  const resolvedPrice = computed(() => price?.value ?? price)

  // og:type=product and product:price:* must use raw useHead — useSeoMeta's
  // ogType union has no 'product' member and there is no product:price:* key.
  // The meta array is a getter so price tags react to the active size variant.
  useHead({
    meta: () => {
      const withTax = resolvedPrice.value?.withTax
      return [
        { property: 'og:type', content: 'product' },
        ...(withTax != null
          ? [
              {
                property: 'product:price:amount',
                content: (withTax / 100).toFixed(2),
              },
              { property: 'product:price:currency', content: currency },
            ]
          : []),
      ]
    },
  })

  useJsonld(() => {
    const imageOptions = {
      sizes: 'sm:100vw md:100vw',
      modifiers: { quality: '75' },
    }
    // $img is a helper from nuxt-image that knows the base URL and how to build the src URL
    const images = product?.images?.map(
      (image) => img?.getImage(image.hash, imageOptions).url,
    )

    const color =
      getFirstAttributeValue(product.attributes, 'color')?.value?.toString() ||
      ''

    // ProductGroup schema
    const productGroupSchema = generateProductGroupSchema({
      product: product,
      price: price.value,
      productMasterKey: product.masterKey,
      productName,
      brandName: (product?.attributes?.brand?.values as any)?.label || '',
      color,
      description: productLongDescription,
      material: getAdvancedAttributeFirstValue(product, 'material')?.toString(),
      url: canonicalBaseUrl.value,
      baseProductUrlForVariants: canonicalBaseUrl.value,
      siblings: product.siblings,
      variants: productVariants,
      images: images,
      organizationName: i18n.t('sd.organisation.name'),
      locale: locale,
    })

    const shippingSchema = getSharedShippingDetails(currency, country)

    // Safari cannot parse a top-level JSON-LD array and throws errors.
    // Using a single object with '@context' and '@graph' ensures compatibility
    // TypeScript cast to JsonLD to satisfy Nuxt types
    return {
      '@context': 'https://schema.org',
      '@graph': [productGroupSchema, shippingSchema],
    } as any
  })

  usePdpHeadLinks(() => product, productAlternateNames)
}
